import {
  ThemeProvider,
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  IconButton,
  Button,
  Alert,
  Tooltip
} from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";
import { DataGrid, GridCellParams } from "@mui/x-data-grid";
import { muiTheme } from "system/styles/muiTheme";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApi, useConfiguration } from "system/hooks";
import { useAuth } from "system/hooks/use-auth";
import { authStorage } from "components/Intune/AuthService";
import { intuneActions } from "system/store/slices/intune-slice";
import { RootState } from "system/store";
import { IntuneActionResponse, IntuneData } from "system/types/wireTypes";
import { ConfirmationDialog } from "components/ui";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import WarningIcon from "@mui/icons-material/Warning";
import { DeleteRegular } from "@fluentui/react-icons";

const subjects = [
  { id: "AIDEN_AIDENBOT", name: "Task-focused package: Aiden AidenBot" },
  { id: "AIDEN_RECOMMENDATIONS", name: "Desired state package: Aiden Recommendations" },
  { id: "AIDEN_CRITICAL_UPDATES", name: "Desired state package: Aiden Critical Updates" },
  { id: "VS_CODE", name: "Company Portal package: Microsoft Visual Studio Code" }
];

const extractErrorMessage = (err: any): string =>
  err?.response?.data ||
  err?.response?.data?.error ||
  err?.message ||
  "Unexpected error";

export const IntuneSetupTab = () => {
  const api = useApi();
  const { getToken } = useAuth();
  const { currentTenant } = useConfiguration();
  const dispatch = useDispatch();
  const isIntuneOn = useSelector((state: RootState) => state.intune.isIntuneOn);

  const customerName = currentTenant?.shortName ?? "";
  const tenant = authStorage.getTenantId();

  const [statusMap, setStatusMap] = useState<Record<string, IntuneData>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<string | null>(null);

  // getSubjects's X-Graph-Token is optional — per the API contract, the endpoint always
  // returns 200 with the last-known DB state if a token is missing or invalid, so (unlike
  // handleCreate/deleteSubject, where the token is mandatory) we don't hard-fail when one
  // isn't available here; we just skip the live Graph check and show cached status instead.
  // Accepts an already-fetched token so callers like deleteSubject that just acquired one
  // don't trigger a second MSAL round-trip for the same user action.
  const loadSubjectsStatus = async (existingToken?: string | null) => {
    if (!isIntuneOn || !tenant || !customerName) return;

    try {
      setLoading(true);
      setErrorMessage("");

      const token = existingToken !== undefined ? existingToken : await getToken();
      const response = await api.intune.getSubjects(tenant, customerName, token ?? undefined);

      const map: Record<string, IntuneData> = {};
      (response ?? []).forEach((item) => {
        if (item.subject) map[item.subject] = item;
      });

      setStatusMap(map);
    } catch (err) {
      console.error("Failed to load subjects", err);
      setErrorMessage("Failed to refresh package status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubjectsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, tenant, isIntuneOn]);

  // Shared shape for saveSubject/deleteSubject: acquire a token, guard on tenant, call the
  // Intune Graph action, and surface its normalized { success, response?, error? } envelope
  // (see IntuneActionResponse) — the two only differ in which API method they call and what
  // to do with the resulting row on success.
  const runIntuneAction = async (
    apiCall: (token: string, tenantId: string) => Promise<IntuneActionResponse<IntuneData>>,
    failureMessage: string,
    onSuccess: (response: IntuneData, token: string) => void | Promise<void>
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const token = await getToken();

      if (!token) {
        setErrorMessage("No token available");
        dispatch(intuneActions.setIntuneOn(false));
        return;
      }

      if (!tenant) {
        setErrorMessage("No tenant selected");
        return;
      }

      const result = await apiCall(token, tenant);

      if (!result.success) {
        setErrorMessage(result.error || failureMessage);
        return;
      }

      await onSuccess(result.response, token);

    } catch (err: any) {
      console.error(err);
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (subjectId: string) =>
    runIntuneAction(
      (token, tenantId) => api.intune.saveSubject(token, tenantId, customerName, subjectId),
      "Failed to create package",
      (response) => setStatusMap(prev => ({ ...prev, [subjectId]: response }))
    );

  const deleteSubject = (subjectId: string) =>
    runIntuneAction(
      (token, tenantId) => api.intune.deleteSubject(token, tenantId, customerName, subjectId),
      "Failed to remove package",
      (_response, token) => loadSubjectsStatus(token)
    );

  // Confirmation-dialog flow — used for a live, newly-created app.
  const handleDeleteClick = (subjectId: string) => {
    setSubjectToDelete(subjectId);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSubjectToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!subjectToDelete) return;
    await deleteSubject(subjectToDelete);
    setDeleteDialogOpen(false);
    setSubjectToDelete(null);
  };

  const columns = [
    {
      field: "name",
      headerName: "Applications",
      flex: 1,
      minWidth: 300
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 150,
      renderCell: (params: GridCellParams) => {
        if (!isIntuneOn) return null;

        const status = statusMap[params.row.id];

        // MUI sets pointer-events: none on disabled buttons, which suppresses any hover
        // cursor on the button itself — wrap it in a span so "not-allowed" still shows.
        if (!status) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
              <span style={{ cursor: isIntuneOn ? "pointer" : "not-allowed" }}>
                <IconButton
                  onClick={() => handleCreate(params.row.id)}
                  color="primary"
                  disabled={!isIntuneOn}
                >
                  <AddCircleIcon style={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Box>
          );
        }

        const isDeleted = status.intuneDeleted;

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
            {isDeleted ? (
              <>
                <Tooltip title="Removed from Intune outside of Aiden.">
                  <span style={{ cursor: "not-allowed" }}>
                    <IconButton color="warning" disabled>
                      <WarningIcon style={{ fontSize: 28 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Add it again">
                  <span style={{ cursor: isIntuneOn ? "pointer" : "not-allowed" }}>
                    <IconButton
                      onClick={() => handleCreate(params.row.id)}
                      color="primary"
                      disabled={!isIntuneOn}
                    >
                      <AddCircleIcon style={{ fontSize: 28 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Deleting an already-deleted row is an idempotent no-op on the backend
                    (it just returns the current row), so skip the confirmation modal here —
                    this icon just clears the stale warning, not a real destructive action. */}
                <Tooltip title="Remove warning" arrow>
                  <span style={{ cursor: isIntuneOn ? "pointer" : "not-allowed" }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteSubject(params.row.id)}
                      disabled={!isIntuneOn}
                    >
                      <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              // Modified-outside-Aiden apps are treated as healthy in this UI — just the
              // tick, no warning affordance — per product feedback. Delete is still available below.
              <CheckCircleIcon style={{ color: "limegreen", fontSize: 28, margin: "8px" }} />
            )}

            {!isDeleted && (
              <Tooltip title="Remove from Intune" arrow>
                <span style={{ cursor: isIntuneOn ? "pointer" : "not-allowed" }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(params.row.id)}
                    disabled={!isIntuneOn}
                  >
                    <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  const rows = subjects.map(s => ({
    id: s.id,
    name: s.name
  }));

  const subjectToDeleteName = subjects.find(s => s.id === subjectToDelete)?.name ?? subjectToDelete;
  const subjectToDeleteAppId = subjectToDelete ? statusMap[subjectToDelete]?.intuneAppId : undefined;

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
          gap: 2,
          mb: 2
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#ffffff"
          }}
        >
          Create Intune Windows Apps
        </Typography>

        <Button className="refresh" variant="outlined" onClick={() => loadSubjectsStatus()} disabled={!isIntuneOn}>
          Refresh
          <div className="refresh-icon">
            <Icon iconName="refresh" />
          </div>
        </Button>

        {!isIntuneOn && (
          <Typography variant="body2" sx={{ color: "#f44336", marginTop: "6px" }}>
            (Disconnected from Intune tenant. Connect to add windows apps.)
          </Typography>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box style={{ height: "600px", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } }
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #424242",
              backgroundColor: "#3c3c3c",
              color: "#ffffff"
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#1e1e1e",
              borderBottom: "1px solid #424242",
              color: "white"
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "#1e1e1e",
              borderTop: "1px solid #424242",
              color: "white"
            }
          }}
        />
      </Box>

      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: 9999,
          backgroundColor: "rgba(0,0,0,0.7)"
        }}
      >
        <CircularProgress />
      </Backdrop>

      <ConfirmationDialog
        isVisible={deleteDialogOpen}
        title="Delete Confirmation"
        subText={`Are you sure you want to remove "${subjectToDeleteName}"${subjectToDeleteAppId ? ` (App ID: ${subjectToDeleteAppId})` : ""} from Intune?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </ThemeProvider>
  );
};